import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Custom hook for OTP input management
 * @param {number} length - Length of OTP code (default: 6)
 * @returns {Object} OTP state and handlers
 */
export const useOtpInput = (length = 6) => {
  const [code, setCode] = useState(Array(length).fill(''));
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleInputChange = useCallback(
    (index, value) => {
      if (!/^\d*$/.test(value)) return;

      const newCode = [...code];
      newCode[index] = value.slice(-1);
      setCode(newCode);

      if (value && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [code, length],
  );

  const handleKeyDown = useCallback(
    (index, e) => {
      if (e.key === 'Backspace' && !code[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [code],
  );

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
      const newCode = Array(length).fill('');

      for (let i = 0; i < length; i++) {
        newCode[i] = pastedData[i] || '';
      }

      setCode(newCode);

      const lastFilledIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[lastFilledIndex]?.focus();
    },
    [length],
  );

  const isComplete = code.every((digit) => digit !== '');
  const otp = code.join('');

  return {
    code,
    otp,
    isComplete,
    inputRefs,
    handleInputChange,
    handleKeyDown,
    handlePaste,
    setCode,
  };
};
